import { AlgoliaIndice } from '@libs/algolia';
import type { MappedProductVariant, ProductHit, ProductVariants } from '@libs/algolia';
import {
    addUpdateAlgoliaEvents,
    deleteEvents,
    getGoogleAnalyticsUserIdFromBrowserCookie,
    getAlgoliaEvents,
    getPDPEventData,
    isOptedOutTracking,
    getMasterProductID,
} from '@libs/analytics';
import { DEVICE_TYPE, useDeviceType, type ItemsListItem } from '@libs/common-next';
import insights from 'search-insights';
import type { AutocompleteItem } from '../../types/algoliaTracking';
import { getCurrency, isUserLoggedIn } from '@libs/core';
import type { ItemUI } from '@libs/common-node';

// Local type for products with productVariants property (like PDP Product type)
type ProductWithVariants = ProductHit & {
    productVariants?: ProductVariants;
};

const algoliaAppId = process.env.NEXT_PUBLIC_ALGOLIA_APP_ID as string;
const algoliaSitemapAPIKey = process.env.NEXT_PUBLIC_ALGOLIA_APP_SEARCH_KEY as string;

export const initAlgoliaTracking = () => {
    if (IS_SSR) return;

    // prevent tracking if opted out of tracking.
    if (isOptedOutTracking()) {
        return;
    }

    if (!algoliaAppId || !algoliaSitemapAPIKey) {
        console.warn('Algolia tracking disabled: Missing environment variables');
        return;
    }

    insights('init', {
        appId: algoliaAppId,
        apiKey: algoliaSitemapAPIKey,
        userToken: getGoogleAnalyticsUserIdFromBrowserCookie('_ga'),
    });
};

export const algoliaTrackClickEvent = (rawProductHit: ProductHit) => {
    if (isOptedOutTracking()) {
        return;
    }

    if (!algoliaAppId || !algoliaSitemapAPIKey) {
        console.warn('Algolia tracking disabled: Missing environment variables');
        return;
    }

    try {
        if (rawProductHit.__queryID) {
            addUpdateAlgoliaEvents(
                {
                    objectID: rawProductHit.objectID,
                    __queryID: rawProductHit.__queryID,
                    masterProductID: rawProductHit.masterProductID,
                },
                false
            );
            insights('clickedObjectIDsAfterSearch', {
                eventName: 'PLP: Product Clicked',
                index: AlgoliaIndice.PRODUCTS,
                objectIDs: [rawProductHit.objectID],
                positions: [rawProductHit.__position],
                queryID: rawProductHit.__queryID,
                userToken: getGoogleAnalyticsUserIdFromBrowserCookie('_ga'),
            });
        }
    } catch (e) {
        console.error('Error in algoliaTrackClickEvent:', e);
    }
};

const getPrice = (product: ProductWithVariants | ProductHit | MappedProductVariant | ItemUI) => {
    if ('price' in product) {
        return product.price?.number;
    }
    if ('listPrice' in product) {
        return product.listPrice;
    }
    return 0;
};
const getPriceValue = (product: ProductWithVariants | ProductHit | MappedProductVariant | ItemUI, quantity: number) => {
    if ('price' in product) {
        return product.price?.number;
    }
    const price = getPrice(product);
    const cost = price !== undefined ? price * quantity : 0;
    const value = Math.round(cost * 100) / 100;
    return value;
};

export const algoliaPDPTrackClickEvent = (
    product: ProductWithVariants | ProductHit | MappedProductVariant | ItemUI | undefined,
    quantity?: number
) => {
    if (isOptedOutTracking()) {
        return;
    }

    if (!product) {
        return;
    }

    if (!quantity) {
        return;
    }

    if (!algoliaAppId || !algoliaSitemapAPIKey) {
        console.warn('Algolia tracking disabled: Missing environment variables');
        return;
    }

    // wrapped in a try/catch to avoid ATC issues.
    try {
        const objectID = 'objectID' in product ? product.objectID : product.sku;
        const masterProductID = getMasterProductID(product);

        const eventData = getPDPEventData(product);
        if (eventData?.queryID) {
            addUpdateAlgoliaEvents(
                { objectID: objectID, __queryID: eventData.queryID, masterProductID: masterProductID },
                true
            );
            insights('addedToCartObjectIDsAfterSearch', {
                userToken: getGoogleAnalyticsUserIdFromBrowserCookie('_ga'),
                eventName: 'PDP: Product Added to Cart after Search',
                index: AlgoliaIndice.PRODUCTS,
                objectIDs: [objectID],
                queryID: eventData.queryID,
                value: getPriceValue(product, quantity),
                currency: getCurrency(),
                objectData: [
                    {
                        price: getPrice(product),
                        quantity: quantity,
                    },
                ],
            });
        } else {
            insights('addedToCartObjectIDs', {
                userToken: getGoogleAnalyticsUserIdFromBrowserCookie('_ga'),
                eventName: 'PDP: Product Added to Cart',
                index: AlgoliaIndice.PRODUCTS,
                objectIDs: [objectID],
                value: getPriceValue(product, quantity),
                currency: getCurrency(),
                objectData: [
                    {
                        price: getPrice(product),
                        quantity: quantity,
                    },
                ],
            });
        }
    } catch (e) {
        console.error('Error in algoliaPDPTrackClickEvent:', e);
    }
};

export const algoliaAutofillTrack = (rawProductHit: AutocompleteItem) => {
    if (isOptedOutTracking()) {
        return;
    }

    if (!algoliaAppId || !algoliaSitemapAPIKey) {
        console.warn('Algolia tracking disabled: Missing environment variables');
        return;
    }

    try {
        if (rawProductHit.__autocomplete_queryID && rawProductHit.__autocomplete_id) {
            addUpdateAlgoliaEvents(
                {
                    objectID: rawProductHit.objectID,
                    __queryID: rawProductHit.__autocomplete_queryID,
                    masterProductID: rawProductHit.masterProductID,
                },
                false
            );
            insights('clickedObjectIDsAfterSearch', {
                eventName: 'Autocomplete: Product Clicked',
                index: AlgoliaIndice.PRODUCTS,
                objectIDs: [rawProductHit.objectID],
                positions: [rawProductHit.__autocomplete_id],
                queryID: rawProductHit.__autocomplete_queryID,
                userToken: getGoogleAnalyticsUserIdFromBrowserCookie('_ga'),
            });
        }
    } catch (e) {
        console.error('Error in algoliaAutofillTrack:', e);
    }
};
export const algoliaCheckoutTrackClickEvent = (rawProductHit: ItemsListItem[]) => {
    if (isOptedOutTracking()) {
        return;
    }

    if (!algoliaAppId || !algoliaSitemapAPIKey) {
        console.warn('Algolia tracking disabled: Missing environment variables');
        return;
    }

    try {
        const algoliaEvents = getAlgoliaEvents();

        if (algoliaEvents && algoliaEvents.length > 0) {
            const addedToCart = algoliaEvents.filter(event => event.addedToCart);

            // Most efficient approach: iterate through purchased products and check algolia events
            const combinedObjectIDs: string[] = [];
            const combinedObjectData: Array<{
                queryID?: string;
                price: number;
                discount: number;
                quantity: number;
            }> = [];

            // Create a map of objectID -> algolia event for O(1) lookup
            const algoliaEventMap = new Map<string, (typeof addedToCart)[0]>();
            addedToCart.forEach(event => {
                if (event.objectIDs.length > 0 && event.addedToCart) {
                    event.objectIDs.forEach(objectID => {
                        algoliaEventMap.set(objectID, event);
                    });
                }
            });

            // Iterate through purchased products and check if they have algolia events
            rawProductHit.forEach(item => {
                const algoliaEvent = algoliaEventMap.get(item.sku);
                if (algoliaEvent) {
                    combinedObjectIDs.push(item.sku);
                    combinedObjectData.push({
                        queryID: algoliaEvent.queryID,
                        price: item.salePrice || item.basePrice,
                        discount: item.basePrice - (item.salePrice || item.basePrice),
                        quantity: item.quantity,
                    });
                }
            });

            // If we found matching combinations, send the tracking event
            const totalValue = combinedObjectData.reduce((sum, item) => sum + item.price * item.quantity, 0);

            insights('purchasedObjectIDsAfterSearch', {
                eventName: 'Conversion: Product Purchased after Search',
                index: AlgoliaIndice.PRODUCTS,
                objectIDs: combinedObjectIDs,
                queryID: addedToCart[0]?.queryID, // Use the first queryID as primary
                userToken: getGoogleAnalyticsUserIdFromBrowserCookie('_ga'),
                currency: getCurrency(),
                objectData: combinedObjectData,
                value: Math.round(totalValue * 100) / 100,
            });

            // Handle products that don't have algolia events or weren't added to cart (fallback)
            const unmatchedSkus = rawProductHit.filter(item => !algoliaEventMap.has(item.sku)).map(item => item.sku);

            if (unmatchedSkus.length > 0) {
                insights('purchasedObjectIDs', {
                    eventName: 'Conversion: Product Purchased',
                    index: AlgoliaIndice.PRODUCTS,
                    objectIDs: unmatchedSkus,
                    userToken: getGoogleAnalyticsUserIdFromBrowserCookie('_ga'),
                    currency: getCurrency(),
                });
            }
        } else {
            // No algolia events - use original fallback logic
            const ids = rawProductHit.map(({ sku }) => sku);
            insights('purchasedObjectIDs', {
                eventName: 'Conversion: Product Purchased',
                index: AlgoliaIndice.PRODUCTS,
                objectIDs: ids,
                userToken: getGoogleAnalyticsUserIdFromBrowserCookie('_ga'),
                currency: getCurrency(),
            });
        }
    } catch (e) {
        console.error('Error in algoliaCheckoutTrackClickEvent:', e);
    } finally {
        // Always cleanup events, even if there was an error
        deleteEvents();
    }
};

export const analyticsTags = (additionalTags: string[] = []) => {
    const loginTag = isUserLoggedIn() ? 'user_loggedin' : 'user_loggedout';
    const deviceTag = useDeviceType() === DEVICE_TYPE.MOBILE ? 'web_mobile' : 'web_desktop';
    return [deviceTag, loginTag, ...additionalTags];
};
